import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getUserOpportunities, getPlaces, deactivateOpportunity, deleteOpportunity, activateOpportunity } from '../api/api';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Tooltip, IconButton, Collapse, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Fab from '@mui/material/Fab';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import OpportunityListItem from './OpportunityListItem';
import { toZonedTime,  format as formatTz } from 'date-fns-tz';

const OpportunityListSkipper = ({ token, domain, profile }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [opportunities, setOpportunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [places, setPlaces] = useState([]);
    const [showActive, setShowActive] = useState(true);
    const [showSuspended, setShowSuspended] = useState(false);
    const [showExpired, setShowExpired] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [opportunityToDelete, setOpportunityToDelete] = useState(null);
    // Check if user is premium
    const isPremium = useMemo(() => {
        return profile?.premiumExp != null && new Date(profile?.premiumExp) > new Date();
    }, [profile]);
    
    // Set opportunity limit based on premium status
    const opportunityLimit = useMemo(() => {
        return isPremium ? 5 : 1;
    }, [isPremium]);
    
    // Check if user has reached their opportunity limit
    const hasReachedLimit = useMemo(() => {
        const activeOpportunitiesCount = opportunities.length;
        return activeOpportunitiesCount >= opportunityLimit;
    }, [opportunities, opportunityLimit]);
    

    const placeIdToName = (id) => {
        const name = places?.find(x => x._id == id)?.name;
        return name ?? "Unknown location"
    }
    const loadOpportunities = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getUserOpportunities(token, undefined);
            setOpportunities(data);
        } catch (error) {
            console.error('Error loading opportunities:', error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    const loadPlaces = useCallback(async () => {
        try {
            const data = await getPlaces(domain);
            setPlaces(data);
        } catch (error) {
            console.error('Error loading opportunities:', error);
        }
    }, [token, domain]);

    useEffect(() => {
        loadOpportunities();
    }, [loadOpportunities]);

    useEffect(() => {
        loadPlaces();
    }, [loadPlaces]);

    const handleOpen = () => {
        navigate('/opportunity');
    };

    const handleEdit = (opportunity) => {
        navigate(`/opportunity/${opportunity._id}`);
    };

    const handleDeleteClick = async (opportunity) => {
        setOpportunityToDelete(opportunity);
        setDeleteDialogOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setOpportunityToDelete(null);
    };

    const handleDeleteConfirm = async () => {
        try {            
            await deleteOpportunity(token, opportunityToDelete._id);
            await loadOpportunities();
            setDeleteDialogOpen(false);
            setOpportunityToDelete(null);
        } catch (error) {
            console.error('Error deleting opportunity:', error);
        }
    };

    const handleActivateChange = async (opportunity, isActive) => {
        if  (isActive) await activateOpportunity(token, opportunity._id)         
            else await deactivateOpportunity(token, opportunity._id);
        await loadOpportunities();
    }


    return (
        <Box sx={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 3 }}>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                {/* Active Opportunities */}
            {opportunities.filter(opp => opp.isActive && new Date(opp.dateTime) > new Date()).length > 0 ? (
                <Box>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ marginBottom: 1, cursor: 'pointer' }}
                        onClick={() => setShowActive(!showActive)}
                    >
                        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                            {t('opportunity.active')} ({opportunities.filter(opp => opp.isActive && new Date(opp.dateTime) > new Date()).length})
                        </Typography>
                        <IconButton size="small">
                            {showActive ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Stack>
                    <Collapse in={showActive}>
                        <List sx={{ width: '100%' }}>
                            {opportunities
                                .filter(opp => opp.isActive && new Date(opp.dateTime) > new Date())
                                .map((opportunity) => (
                                    <OpportunityListItem
                                        key={opportunity._id}
                                        opportunity={opportunity}
                                        placeIdToName={placeIdToName}
                                        onEdit={handleEdit}
                                        onDelete={handleDeleteClick}
                                        onChangeActivate={handleActivateChange}
                                        badgeColor='green'
                                        hint={t('opportunity.activeHint')}
                                        token={token}
                                    />
                                ))}
                        </List>
                    </Collapse>
                </Box>
            ) : (
                <Typography variant="h6" color="textSecondary" align="center" sx={{ marginTop: '40px' }}>
                    {t('opportunity.noActive')}
                </Typography>
            )}

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Tooltip 
                    title={
                        hasReachedLimit 
                            ? isPremium 
                                ? <Trans i18nKey="opportunity.premiumLimitReached" values={{ limit: opportunityLimit }} />
                                : <Trans i18nKey="opportunity.limitReached" values={{ limit: opportunityLimit }} />
                            : ""
                    }
                    arrow
                >
                    <span>
                        <Fab
                           
                            color="primary"
                            aria-label="add"
                            onClick={handleOpen}
                            disabled={hasReachedLimit}
                            sx={{
                                zIndex: 1,
                                opacity: hasReachedLimit ? 0.6 : 1,
                                '&.Mui-disabled': {
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    opacity: 0.6
                                }
                            }}
                        >
                            <AddIcon />
                        </Fab>
                    </span>
                </Tooltip>
            </Box>

            {/* Suspended Opportunities */}
            {opportunities.filter(opp => !opp.isActive && new Date(opp.dateTime) > new Date()).length > 0 && (
                <Box sx={{ marginTop: 2 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ marginBottom: 1, cursor: 'pointer' }}
                        onClick={() => setShowSuspended(!showSuspended)}
                    >
                        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                            {t('opportunity.suspended')} ({opportunities.filter(opp => !opp.isActive && new Date(opp.dateTime) > new Date()).length})
                        </Typography>
                        <IconButton size="small">
                            {showSuspended ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Stack>
                    <Collapse in={showSuspended}>
                        <List sx={{ width: '100%' }}>
                            {opportunities
                                .filter(opp => !opp.isActive && new Date(opp.dateTime) > new Date())
                                .map((opportunity) => (
                                    <OpportunityListItem
                                        key={opportunity._id}
                                        opportunity={opportunity}
                                        placeIdToName={placeIdToName}
                                        onEdit={handleEdit}
                                        onDelete={handleDeleteClick}
                                        onChangeActivate={handleActivateChange}
                                        badgeColor='orange'
                                        hint={t('opportunity.suspendedHint')}
                                        token={token}
                                    />
                                ))}
                        </List>
                    </Collapse>
                </Box>
            )}

            {/* Expired Opportunities */}
            {opportunities.filter(opp => new Date(opp.dateTime) <= new Date()).length > 0 && (
                <Box sx={{ marginTop: 2 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ marginBottom: 1, cursor: 'pointer' }}
                        onClick={() => setShowExpired(!showExpired)}
                    >
                        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                            {t('opportunity.expired')} ({opportunities.filter(opp => new Date(opp.dateTime) <= new Date()).length})
                        </Typography>
                        <IconButton size="small">
                            {showExpired ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Stack>
                    <Collapse in={showExpired}>
                        <List sx={{ width: '100%' }}>
                            {opportunities
                                .filter(opp => new Date(opp.dateTime) <= new Date())
                                .map((opportunity) => (
                                    <OpportunityListItem
                                        key={opportunity._id}
                                        opportunity={opportunity}
                                        placeIdToName={placeIdToName}
                                        onEdit={handleEdit}
                                        onDelete={handleDeleteClick}
                                        onChangeActivate={undefined}
                                        badgeColor='red'
                                        hint={t('opportunity.expiredHint')}
                                        token={token}
                                    />
                                ))}
                        </List>
                    </Collapse>
                </Box>
            )}


            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>{t('opportunity.deleteTitle')}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {t('opportunity.deleteConfirm')}
                        {opportunityToDelete && (
                            <Box sx={{ mt: 2, fontWeight: 'bold' }}>
                                {opportunityToDelete.name || 
                                      formatTz(toZonedTime(opportunityToDelete.dateTime, opportunityToDelete.tz), "PPP", { timeZone: opportunityToDelete.tz })}
                            </Box>
                        )}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel}>{t('opportunity.form.cancel')}</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        {t('opportunity.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
                </>
            )}
        </Box>
    );
};

export default OpportunityListSkipper;
